import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import { GroupHeader } from "./Panel";
import { PriorityBadge } from "./Priority";
import { OrderInput } from "./OrderInput";
import { formatDate, shortName } from "../data/format";
import { NEXT_TONE_CLASS } from "../data/status";
import { StatusLabel } from "./StatusDot";
import { statusGroups } from "../data/requestGroups";
import type { GroupSpec } from "../data/requestGroups";
import type { PortalRequestSummary, RequestSummary } from "../data/types";

/** Ten columns; the two person tracks are the account lead and the technical owner. */
const STAFF_GRID = "grid-cols-[26px_56px_60px_minmax(0,1fr)_120px_150px_90px_90px_100px_100px]";
/** The By-technical-owner view: the group already names the owner, so its
 *  track shows the status and the next the account lead. The status track is
 *  wider — "Waiting on customer" and "Escalated Core Eng." overflow 90px — and
 *  the title column absorbs it, so the table is no wider. */
const STAFF_GRID_STATUS = "grid-cols-[26px_56px_60px_minmax(0,1fr)_120px_150px_150px_90px_100px_100px]";
const PORTAL_GRID = "grid-cols-[26px_56px_60px_minmax(0,1fr)_150px_110px_100px_100px]";
const ROW =
  "grid min-h-9 items-center gap-3 border-b border-[var(--border)] px-4 text-sm transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:focus-ring";
/** The whole staff row navigates: the title link stretches over the row, and
 *  the one control that must not navigate (the Order box) sits above it. */
const STRETCHED_LINK = "truncate focus-visible:focus-ring after:absolute after:inset-0 after:content-['']";
const HEAD = "grid gap-3 border-b border-[var(--border)] bg-gray-100 px-4 py-2 text-xs font-medium text-[var(--text-2)]";

/** How the rows are ordered; `null` is the default (priority, then newest). */
export type ListSort = "next_asc" | "next_desc" | null;

type Props = (
  | {
      variant: "staff";
      rows: RequestSummary[];
      /** How to group. Omitted → by status, as every staff list did before.
       *  There is no portal equivalent: PortalRequestSummary has no owner. */
      group?: GroupSpec<RequestSummary>;
    }
  | { variant: "portal"; rows: PortalRequestSummary[] }
) & {
  linkTo: (id: string) => string;
  emptyTitle?: string;
  emptyBody?: string;
  /** The current commitment-date sort and how to change it. Omitted → the header is plain text. */
  sort?: ListSort;
  onSort?: (sort: ListSort) => void;
  /** Lets the Order box in each row save. Omitted → the order is read-only text.
   *  On the portal the box is live only for rows the customer may order (New). */
  onOrderChange?: (id: string, order: number | null) => void;
};

/** The customer's needed-by date, or a dash. */
function NeededByCell({ value }: { value: string | null }) {
  return <span className="text-right text-xs text-[var(--text-2)]">{value ? formatDate(value) : "—"}</span>;
}

/**
 * The soonest milestone this request must be ready for.
 *
 * Replaces the Status column, which repeated the group header every row sits
 * under. A request whose milestones have all passed reads as overdue, because
 * it is: work still open for a date that has been and gone.
 */
function MilestoneCell({ cell }: { cell: RequestSummary["milestone"] }) {
  if (!cell) return <span className="text-xs text-[var(--text-3)]">—</span>;
  return (
    <span className={`truncate text-xs ${NEXT_TONE_CLASS[cell.dateTone]}`} title={cell.more > 0 ? `${cell.title} (+${cell.more} more)` : cell.title}>
      {cell.dateLabel}
      {cell.more > 0 && <span className="ml-1 text-[var(--text-3)]">+{cell.more}</span>}
    </span>
  );
}

/** The Order cell: a live box when the row may be ordered here, else the number as text. */
function OrderCell({ id, order, onChange }: { id: string; order: number | null; onChange?: (id: string, order: number | null) => void }) {
  if (!onChange) return <span className="text-center text-xs text-[var(--text-2)]">{order ?? "—"}</span>;
  return <OrderInput value={order} ariaLabel={`Order for ${id}`} onCommit={(next) => onChange(id, next)} className="relative z-10 h-7 w-12 px-2 text-center text-xs" />;
}

/** Click the Commitment date header: none → soonest first → latest first → none. */
function nextSort(current: ListSort): ListSort {
  return current === null ? "next_asc" : current === "next_asc" ? "next_desc" : null;
}

function SortableHeader({ label, sort, onSort }: { label: string; sort: ListSort; onSort?: (s: ListSort) => void }) {
  if (!onSort) return <span className="text-right">{label}</span>;
  const active = sort !== null;
  return (
    <span className="text-right" aria-sort={sort === "next_asc" ? "ascending" : sort === "next_desc" ? "descending" : "none"}>
      <button
        type="button"
        onClick={() => onSort(nextSort(sort))}
        title={sort === null ? "Sort by commitment date, soonest first" : sort === "next_asc" ? "Sort latest first" : "Clear sort"}
        className={`inline-flex items-center gap-1 rounded-sm focus-visible:focus-ring ${active ? "text-[var(--text)]" : "hover:text-[var(--text)]"}`}
      >
        {label}
        <Icon name="chevrons-ud" className={active ? "text-brand-600" : "text-[var(--text-3)]"} />
        {active && <span className="sr-only">{sort === "next_asc" ? "sorted soonest first" : "sorted latest first"}</span>}
      </button>
    </span>
  );
}

/**
 * Which groups a viewer folded away, per list and per grouping.
 *
 * A per-browser convenience: storage can be missing or throw (private windows,
 * blocked site data), and then every group simply starts open. Stored ids are
 * only shape-checked, never matched against a fixed set — an id for a retired
 * status or a departed engineer matches no rendered group and is inert. They
 * are deliberately NOT pruned on write: the roster is empty for the first
 * frames after mount, so pruning would wipe everyone's folds.
 */
export function useCollapsedGroups(key: string): [Set<string>, (group: string) => void] {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : []);
    } catch {
      return new Set();
    }
  });
  const toggle = (group: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      try {
        window.localStorage.setItem(key, JSON.stringify([...next]));
      } catch {
        // Not remembered across visits; the toggle still works now.
      }
      return next;
    });
  return [collapsed, toggle];
}

/** The grouped body: a heading per bucket — a toggle that folds its rows away,
 *  count still showing — then that bucket's rows. */
function Groups<T extends { id: string }>({ spec, rows, renderRow }: { spec: GroupSpec<T>; rows: T[]; renderRow: (row: T) => ReactNode }) {
  const [collapsed, toggle] = useCollapsedGroups(`ce-hub:${spec.storageKey}:${spec.scope}`);
  return (
    <>
      {spec.build(rows).map((group) => {
        const id = `${spec.scope}-group-${group.key}`;
        const isCollapsed = collapsed.has(group.key);
        return (
          <div key={group.key}>
            <GroupHeader
              label={group.label}
              count={group.rows.length}
              hint={group.hint}
              collapse={{ collapsed: isCollapsed, onToggle: () => toggle(group.key), controls: id }}
            />
            <div id={id} hidden={isCollapsed}>
              {group.rows.map(renderRow)}
            </div>
          </div>
        );
      })}
    </>
  );
}

/**
 * The status-grouped request grid, shared by the staff Requests page and the
 * customer portal. The variant is a discriminated union, not a set of flags:
 * the staff rows carry priority, account lead, technical owner and commitment date and the portal rows do
 * not, so the component cannot render for a customer what the portal API
 * never sent.
 */
export function RequestList(props: Props) {
  const { linkTo, emptyTitle = "No requests here", emptyBody = "Nothing matches these filters." } = props;
  // Status grouping is the default for every staff list; only the Requests
  // page's By-tech-owner tab passes anything else.
  const staffSpec = (props.variant === "staff" && props.group) || statusGroups<RequestSummary>("staff", "staff");

  if (props.rows.length === 0) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--bg)] p-12 text-center">
        <h3 className="text-md font-medium text-gray-900">{emptyTitle}</h3>
        <p className="mt-1 text-sm text-[var(--text-2)]">{emptyBody}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg)]">
      {props.variant === "staff" ? (
        <>
          <div className={`${HEAD} ${staffSpec.showStatus ? STAFF_GRID_STATUS : STAFF_GRID}`}>
            <span />
            <span title="Order within the priority, 1 first">Order</span>
            <span>ID</span>
            <span>Request</span>
            <span>Account</span>
            <span>Milestone</span>
            <span>{staffSpec.showStatus ? "Status" : "Account lead"}</span>
            <span>{staffSpec.showStatus ? "Account lead" : "Technical owner"}</span>
            <span className="text-right">Needed by</span>
            <SortableHeader label="Commitment date" sort={props.sort ?? null} onSort={props.onSort} />
          </div>
          <Groups
            spec={staffSpec}
            rows={props.rows}
            renderRow={(r) => <StaffRow key={r.id} request={r} to={linkTo(r.id)} onOrderChange={props.onOrderChange} showStatus={staffSpec.showStatus} />}
          />
        </>
      ) : (
        <>
          <div className={`${HEAD} ${PORTAL_GRID}`}>
            <span />
            <span title="Your order within the priority, 1 first — while the request is New">Order</span>
            <span>ID</span>
            <span>Request</span>
            <span>Milestone</span>
            <span className="text-right">Updated</span>
            <span className="text-right">Needed by</span>
            <SortableHeader label="Commitment date" sort={props.sort ?? null} onSort={props.onSort} />
          </div>
          <Groups
            spec={statusGroups<PortalRequestSummary>("portal", "customer")}
            rows={props.rows}
            renderRow={(r) => <PortalRow key={r.id} request={r} to={linkTo(r.id)} onOrderChange={props.onOrderChange} />}
          />
        </>
      )}
    </div>
  );
}

/** A person's short name, or a dash named for screen readers ("No account lead"). */
function PersonCell({ person, emptyLabel }: { person: RequestSummary["accountLead"]; emptyLabel: string }) {
  if (!person) return <span aria-label={emptyLabel} className="text-[var(--text-3)]">—</span>;
  return (
    <span className="truncate text-xs text-[var(--text-2)]" title={person.name}>
      {shortName(person.name)}
    </span>
  );
}

function StaffRow({ request, to, onOrderChange, showStatus }: { request: RequestSummary; to: string; onOrderChange?: (id: string, order: number | null) => void; showStatus?: boolean }) {
  return (
    <div className={`${ROW} ${showStatus ? STAFF_GRID_STATUS : STAFF_GRID} relative`}>
      <PriorityBadge priority={request.priority} />
      <OrderCell id={request.id} order={request.order} onChange={onOrderChange} />
      <span className="text-xs text-[var(--text-3)]">{request.id}</span>
      <Link to={to} className={STRETCHED_LINK}>
        {request.title}
      </Link>
      <span className="truncate text-[var(--text-2)]">{request.account}</span>
      <MilestoneCell cell={request.milestone} />
      {showStatus ? <StatusLabel status={request.status} /> : <PersonCell person={request.accountLead} emptyLabel="No account lead" />}
      {showStatus ? <PersonCell person={request.accountLead} emptyLabel="No account lead" /> : <PersonCell person={request.owner} emptyLabel="No technical owner" />}
      <NeededByCell value={request.neededBy} />
      <span className={`text-right text-xs ${NEXT_TONE_CLASS[request.nextTone]}`}>{request.nextLabel}</span>
    </div>
  );
}

function PortalRow({ request, to, onOrderChange }: { request: PortalRequestSummary; to: string; onOrderChange?: (id: string, order: number | null) => void }) {
  return (
    <div className={`${ROW} ${PORTAL_GRID} relative`}>
      <PriorityBadge priority={request.priority} />
      <OrderCell id={request.id} order={request.order} onChange={request.orderEditable ? onOrderChange : undefined} />
      <span className="text-xs text-[var(--text-3)]">{request.id}</span>
      <Link to={to} className={STRETCHED_LINK}>
        {request.title}
      </Link>
      <MilestoneCell cell={request.milestone} />
      <span className="text-right text-xs text-[var(--text-2)]">{formatDate(request.updatedAt)}</span>
      <NeededByCell value={request.neededBy} />
      <span className={`text-right text-xs ${NEXT_TONE_CLASS[request.nextTone]}`}>{request.nextLabel}</span>
    </div>
  );
}
